import React, { useState, useEffect } from 'react';
import apiClient from '../apiClient';
import './ContactForm.css';

const ContactForm = ({ contact, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    source: '',
    company_id: null
  });
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const [showNewCompanyForm, setShowNewCompanyForm] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    industry: '',
    website: '',
    city: '',
    state: '',
    country: ''
  });

  // Initialize form data when contact prop changes
  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        phone: contact.phone || '',
        email: contact.email || '',
        source: contact.source || '',
        company_id: contact.company_id || null
      });

      if (contact.company_id) {
        const fetchCompanyDetails = async () => {
          try {
            const response = await apiClient.get(`/api/companies/${contact.company_id}`);
            setSelectedCompany(response.data);
          } catch (error) {
            console.error('Error fetching company details:', error);
          }
        };
        fetchCompanyDetails();
      }
    }
  }, [contact]);

  // Fetch companies and filter them based on search
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await apiClient.get('/api/companies');
        setCompanies(response.data);
        setFilteredCompanies(response.data);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    fetchCompanies();
  }, []);

  // Filter companies based on search term
  useEffect(() => {
    if (companySearch) {
      const filtered = companies.filter(company =>
        company.name.toLowerCase().includes(companySearch.toLowerCase()) ||
        company.industry?.toLowerCase().includes(companySearch.toLowerCase())
      );
      setFilteredCompanies(filtered);
    } else {
      setFilteredCompanies(companies);
    }
  }, [companySearch, companies]);

  const handleCompanyChange = async (e) => {
    const companyId = e.target.value;
    setFormData(prev => ({ ...prev, company_id: companyId || null }));

    if (companyId) {
      try {
        const response = await apiClient.get(`/api/companies/${companyId}`);
        setSelectedCompany(response.data);
      } catch (error) {
        console.error('Error fetching company details:', error);
        setSelectedCompany(null);
      }
    } else {
      setSelectedCompany(null);
    }
  };

const handleNewCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.post('/api/companies/add-company', newCompany);
      // Add the new company to our list
      setCompanies([...companies, response.data]);
      setFilteredCompanies([...companies, response.data]);
      // Select the new company
      setFormData(prev => ({ ...prev, company_id: response.data.id }));
      setSelectedCompany(response.data);
      // Reset and close the form
      setNewCompany({
        name: '',
        industry: '',
        website: '',
        city: '',
        state: '',
        country: ''
      });
      setShowNewCompanyForm(false);
      setCompanySearch('');
    } catch (error) {
      console.error('Error creating company:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      let response;
      if (contact) {
        // Update existing contact
        response = await apiClient.put(`/api/contacts/${contact.id}`, formData);
      } else {
        // Create new contact
        response = await apiClient.post('/api/contacts', formData)
      }
      
      setSubmitStatus({
        success: true,
        message: `Contact ${contact ? 'updated' : 'saved'} successfully!`
      });
      
      // Reset form if not editing
      if (!contact) {
        setFormData({
          name: '',
          phone: '',
          email: '',
          source: '',
          company_id: null
        });
        setSelectedCompany(null);
      }
      
      // Notify parent component
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: `Failed to ${contact ? 'update' : 'save'} contact. Please try again.`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-container">
      {submitStatus && (
        <div className={`alert ${submitStatus.success ? 'alert-success' : 'alert-error'}`}>
          {submitStatus.message}
        </div>
      )}
      
      {/* Main Contact Form */}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name*</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="source">Source</label>
          <input
            type="text"
            id="source"
            name="source"
            value={formData.source}
            onChange={handleChange}
            placeholder="How did you find us?"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="company_id">Company</label>
          <div className="company-select-container">
            <input
              type="text"
              placeholder="Search companies..."
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="company-search-input"
            />
            <select
              id="company_id"
              name="company_id"
              value={formData.company_id || ''}
              onChange={handleCompanyChange}
              className="company-select"
            >
              <option value="">Select a company...</option>
              {filteredCompanies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
            <button 
              type="button" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowNewCompanyForm(true);
              }}
              className="btn add-company-btn"
            >
              + Create New Company
            </button>
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => onSuccess && onSuccess(null)}
            className="btn cancel-btn"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={isSubmitting}
            className="btn submit-btn"
          >
            {isSubmitting 
              ? contact ? 'Updating...' : 'Saving...' 
              : contact ? 'Update Contact' : 'Save Contact'}
          </button>
        </div>
      </form>

      {/* Company Creation Modal - Outside the contact form */}
      {showNewCompanyForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Create New Company</h2>
              <button 
                onClick={() => setShowNewCompanyForm(false)}
                className="btn close-btn"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleNewCompanySubmit}>
              <div className="form-group">
                <label>Company Name*</label>
                <input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Industry</label>
                <input
                  type="text"
                  value={newCompany.industry}
                  onChange={(e) => setNewCompany({...newCompany, industry: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={newCompany.website}
                  onChange={(e) => setNewCompany({...newCompany, website: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={newCompany.city}
                    onChange={(e) => setNewCompany({...newCompany, city: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>State/Region</label>
                  <input
                    type="text"
                    value={newCompany.state}
                    onChange={(e) => setNewCompany({...newCompany, state: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={newCompany.country}
                    onChange={(e) => setNewCompany({...newCompany, country: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowNewCompanyForm(false)}
                  className="btn cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn submit-btn"
                >
                  Create Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactForm;